import { useSyncExternalStore } from "react";

// A subscribe that never fires — the value is read once at hydration and isn't
// expected to change for the component's lifetime. Module-level so the
// reference is stable across renders (a fresh fn each render would resubscribe).
const noopSubscribe = () => () => {};

/**
 * Read a client-only value SSR-safely, WITHOUT an effect + setState.
 *
 * `getClient` runs on the client (post-hydration); `server` is the value the
 * prerendered / SSR HTML ships (and what `getClient` should converge to or
 * upgrade from). This is the canonical replacement for the
 * `useState(server) + useEffect(() => setState(getClient()), [])` mount-read
 * pattern that trips `react-hooks/set-state-in-effect` — it reads through
 * `useSyncExternalStore` instead, which React designed for exactly this
 * (an external, non-React value that must stay SSR-consistent).
 *
 * Use only for values that are read once and don't need live updates (window
 * history length, an initial `window.location` snapshot). For values that DO
 * change and must re-render (matchMedia, storage events), pass a real
 * `subscribe` to `useSyncExternalStore` directly instead.
 *
 * `getClient` must return a referentially-stable result for unchanged input
 * (primitives compare by value, so a string/boolean is fine; don't return a
 * fresh object/array each call or it will re-render every commit).
 */
export function useClientSnapshot<T>(getClient: () => T, server: T): T {
  return useSyncExternalStore(noopSubscribe, getClient, () => server);
}
